import React, { useState } from 'react';
import { Box, Typography, Drawer, Button } from '@mui/material';
import { SimpleForm, useDataProvider, useNotify, useRefresh, Toolbar, SaveButton, useTranslate } from 'react-admin';
import PropTypes from 'prop-types';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import { useImportErrorHandler } from '@/composables/useImportErrorHandler';
import { styled } from '@mui/material/styles';
import { Palette } from '@/themes/themeOverrides/Palette';
import ImportFormContent from '@/components/global/import/ImportFormContent.jsx';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';

const CustomToolbar = ({ onClose, showValidationDetails, translate }) => (
    <Toolbar>
        <Button onClick={onClose} color='secondary' startIcon={<ArrowLeftIcon />}>
            {translate('common.cancel')}
        </Button>
        {showValidationDetails && <SaveButton label={translate('common.importSection.verifyData')} />}
        {!showValidationDetails && <SaveButton label={translate('common.importSection.saveData')} />}
    </Toolbar>
);

const StyledBox = styled(Box)({
    '&.popup-box--import': {
        '& form > .MuiCardContent-root': {
            margin: '0 !important',
        },
        '& .MuiToolbar-root': {
            justifyContent: 'space-between',
            marginTop: '32px',
            '& .MuiButton-containedPrimary': {
                padding: '10px 20px',
                fontWeight: '600',
                '& svg, & span': {
                    display: 'none !important',
                },
            },
        },
        '& .import-box': {
            width: '100%',
            position: 'relative',
            paddingBottom: '12px',
            '&::before': {
                content: '""',
                position: 'absolute',
                height: '2px',
                backgroundColor: Palette.gray.gray100,
                bottom: 0,
                left: '-56px',
                width: 'calc(100% + 112px)',
            },
            '&.checking': {
                '& .RaFileInput-dropZone': {
                    backgroundColor: Palette.white,
                },
            },
            '& .RaLabeled-label': {
                display: 'none',
            },
            '& .import-success-box': {
                position: 'absolute',
                top: '2px',
                margin: '2px',
                background: Palette.white,
                height: 'calc(100% - 48px)',
                padding: '0 48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                [`@media (max-width: ${breakpoints.values.md}px)`]: {
                    background: 'transparent',
                    position: 'relative',
                    top: 'auto',
                    margin: '0 0 10px 0',
                    padding: '0',
                },
            },
        },
    },
});

const ImportDrawer = ({ open, onClose, title, resource, dataTransformer, placeholderText, showVerifyOnly = true, showValidationDetails = false }) => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const [loading, setLoading] = useState(false);
    const notify = useNotify();
    const refresh = useRefresh();
    const errorHandler = useImportErrorHandler();
    const [isValidationOpen, setIsValidationOpen] = useState(false);
    const [validationDetails, setValidationDetails] = useState(null);
    const [importedCount, setImportedCount] = useState(null);

    const resetForm = () => {
        setLoading(false);
        setValidationDetails(null);
        setImportedCount(null);
    };

    const submit = async data => {
        const formData = new FormData();
        formData.append('source', data.file.rawFile);
        formData.append('sourceType', 'csv');
        if (resource === 'product') {
            formData.append('dataTransformerName', 'csv-product');
        } else {
            formData.append('dataTransformerName', dataTransformer);
        }

        if (showVerifyOnly && !showValidationDetails) {
            formData.append('isVerifyOnly', data.isVerifyOnly);
        }

        if (showValidationDetails) {
            try {
                setLoading(true);
                await dataProvider
                    .import(`${resource}/import/verify`, { formData })
                    .then(result => {
                        setValidationDetails(result?.data?.data ?? result?.data ?? result);
                        setIsValidationOpen(true);
                        if (result && result.status > 200 && result.status < 300) {
                            notify(translate('common.importSection.success'), { type: 'success' });
                            onClose();
                            refresh();
                        }
                    })
                    .catch(errorHandler)
                    .finally(() => setLoading(false));
            } catch (error) {
                notify(error.message, { type: 'error', autoHideDuration: null });
                setLoading(false);
            }
        } else {
            try {
                setLoading(true);
                await dataProvider
                    .import(`${resource}/import`, { formData })
                    .then(result => {
                        if (showVerifyOnly && data.isVerifyOnly) {
                            notify(translate('common.importSection.noProblems'), { type: 'success' });
                        }
                        if (result && result.status > 200 && result.status < 300) {
                            notify(translate('common.importSection.success'), { type: 'success' });
                            onClose();
                            refresh();
                        }
                    })
                    .catch(errorHandler)
                    .finally(() => setLoading(false));
            } catch (error) {
                notify(error.message, { type: 'error', autoHideDuration: null });
                setLoading(false);
            }
        }
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            anchor='right'
            PaperProps={{
                className: 'custom-dialog-right custom-dialog-right--no-resizer',
                sx: {
                    maxWidth: '512px !important',
                },
            }}
        >
            <Box className='custom-dialog-right__inner'>
                <Box className='popup-wrapper popup-wrapper-top'>
                    <Box className='popup-box popup-box--header'>
                        <Typography variant='h4'>{title}</Typography>
                        <Box>
                            <CustomCloseButton onClose={onClose} />
                        </Box>
                    </Box>
                </Box>
                <Box className='popup-wrapper'>
                    <StyledBox className='popup-box popup-box--import'>
                        <SimpleForm
                            onSubmit={submit}
                            reValidateMode='onBlur'
                            toolbar={<CustomToolbar onClose={onClose} showValidationDetails={showValidationDetails} translate={translate} />}
                        >
                            <ImportFormContent
                                onSubmit={submit}
                                onClose={onClose}
                                showValidationDetails={showValidationDetails}
                                showVerifyOnly={showVerifyOnly}
                                placeholderText={placeholderText}
                                loading={loading}
                                isValidationOpen={isValidationOpen}
                                validationDetails={validationDetails}
                                resetForm={resetForm}
                                setIsValidationOpen={setIsValidationOpen}
                                setLoading={setLoading}
                                resource={resource}
                                dataTransformer={dataTransformer}
                                importedCount={importedCount}
                                setImportedCount={setImportedCount}
                                open={open}
                            />
                        </SimpleForm>
                    </StyledBox>
                </Box>
            </Box>
        </Drawer>
    );
};

ImportDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    resource: PropTypes.string.isRequired,
    dataTransformer: PropTypes.string.isRequired,
    placeholderText: PropTypes.string.isRequired,
    showVerifyOnly: PropTypes.bool,
    showValidationDetails: PropTypes.bool,
};

export default ImportDrawer;
